import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useQuery, useMutation } from '@tanstack/react-query';

import { colors } from '../../theme/tokens';
import { syncPlatformApi, SyncCandidate, SyncResult } from '../../api/syncPlatformApi';
import { WriteButton } from '../../components/ui';

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: (tenantCode: string) => void;
}

type Step = 'pick' | 'preview' | 'result';

const headerSx = { fontSize: 22, fontWeight: 700, fontFamily: 'Manrope, sans-serif', color: colors.textPrimary };
const subSx = { fontSize: 13, color: colors.textSecondary, mt: 0.5 };

const SyncFromPlatformModal: React.FC<Props> = ({ open, onClose, onImported }) => {
  const [step, setStep] = useState<Step>('pick');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SyncCandidate | null>(null);
  const [preview, setPreview] = useState<SyncResult | null>(null);
  const [applied, setApplied] = useState<SyncResult | null>(null);

  const candidatesQuery = useQuery({
    queryKey: ['sync-platform-candidates'],
    queryFn: () => syncPlatformApi.candidates(),
    enabled: open,
    staleTime: 60 * 1000,
  });

  const previewMutation = useMutation({
    mutationFn: (code: string) => syncPlatformApi.preview(code),
    onSuccess: (data) => { setPreview(data); setStep('preview'); },
  });

  const applyMutation = useMutation({
    mutationFn: (code: string) => syncPlatformApi.apply(code),
    onSuccess: (data) => { setApplied(data); setStep('result'); },
  });

  const reset = () => {
    setStep('pick');
    setSearch('');
    setSelected(null);
    setPreview(null);
    setApplied(null);
    previewMutation.reset();
    applyMutation.reset();
  };

  const handleClose = () => { reset(); onClose(); };

  const filtered = useMemo(() => {
    const base = candidatesQuery.data || [];
    if (!search) return base;
    const q = search.toLowerCase();
    return base.filter(
      (c) =>
        c.tenant_name.toLowerCase().includes(q) ||
        c.tenant_code.toLowerCase().includes(q) ||
        String(c.tenant_id).includes(q),
    );
  }, [candidatesQuery.data, search]);

  const errMessage = (e: unknown): string => {
    const anyE = e as any;
    return anyE?.response?.data?.message || anyE?.message || 'Something went wrong';
  };

  // ── Step renderers ─────────────────────────────────────────────────────────

  const renderPick = () => (
    <>
      <Typography sx={headerSx}>Sync from Platform</Typography>
      <Typography sx={subSx}>
        Bring an existing platform tenant under client-portal management. Select a tenant below to
        preview exactly what will be imported into config before syncing.
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Search by name, code, or ID"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mt: 2 }}
        InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>) }}
      />

      <Box sx={{ mt: 2, maxHeight: 360, overflowY: 'auto', border: `1px solid ${colors.borderLight}`, borderRadius: '10px' }}>
        {candidatesQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
        ) : candidatesQuery.isError ? (
          <Alert severity="error" sx={{ m: 2 }}>Failed to load platform tenants: {errMessage(candidatesQuery.error)}</Alert>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', color: colors.textSecondary, fontSize: 13 }}>
            No platform tenants are missing from config{search ? ' for this search' : ''}.
          </Box>
        ) : (
          filtered.map((c) => {
            const isSel = selected?.tenant_code === c.tenant_code;
            return (
              <Box
                key={c.tenant_code}
                onClick={() => setSelected(c)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                  borderBottom: `1px solid ${colors.borderLight}`, cursor: 'pointer',
                  bgcolor: isSel ? colors.bgMuted : 'transparent',
                  '&:hover': { bgcolor: colors.bgMuted },
                }}
              >
                <Radio checked={isSel} size="small" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary }}>{c.tenant_name}</Typography>
                  <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>{c.tenant_code} · id {c.tenant_id}</Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {previewMutation.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>Preview failed: {errMessage(previewMutation.error)}</Alert>
      )}
    </>
  );

  const renderEntityTable = (r: SyncResult, applyMode: boolean) => (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Entity</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>Platform rows</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>{applyMode ? 'Imported' : 'Would import'}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {r.entities.filter((e) => e.platformRows > 0 || e.importedRows > 0).map((e) => (
          <TableRow key={e.entity}>
            <TableCell sx={{ fontSize: 13 }}>{e.entity}</TableCell>
            <TableCell align="right" sx={{ fontSize: 13 }}>{e.platformRows}</TableCell>
            <TableCell align="right" sx={{ fontSize: 13 }}>{e.importedRows}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const renderExclusions = (r: SyncResult) =>
    r.excluded.length > 0 && (
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>Not imported</Typography>
        {r.excluded.map((x) => (
          <Typography key={x.entity} sx={{ fontSize: 12, color: colors.textSecondary, mt: 0.5 }}>
            <strong>{x.entity}</strong> — {x.reason}
          </Typography>
        ))}
      </Box>
    );

  const renderPreview = () => {
    if (!preview) return null;
    return (
      <>
        <Typography sx={headerSx}>Preview — {preview.platformTenant?.tenant_name}</Typography>
        <Typography sx={subSx}>
          This is a dry-run. Nothing has been written yet. Review what will be imported into config, then sync.
        </Typography>
        {renderEntityTable(preview, false)}
        {renderExclusions(preview)}
        {preview.warnings.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {preview.warnings.map((w, i) => (<div key={i}>{w}</div>))}
          </Alert>
        )}
        {applyMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>Sync failed: {errMessage(applyMutation.error)}</Alert>
        )}
      </>
    );
  };

  const renderResult = () => {
    if (!applied) return null;
    const v = applied.verification;
    const noop = v?.rePublishWouldBeNoop;
    return (
      <>
        <Typography sx={headerSx}>Synced — {applied.platformTenant?.tenant_name}</Typography>
        <Typography sx={subSx}>
          {applied.tenantCode} is now in config and can be edited and published.
        </Typography>

        {noop === true && (
          <Alert icon={<CheckCircleOutlineIcon />} severity="success" sx={{ mt: 2 }}>
            Verified: publishing this tenant now would be a <strong>no-op</strong> — it would not change any
            live platform data. Safe to edit and publish.
          </Alert>
        )}
        {noop === false && (
          <Alert icon={<WarningAmberIcon />} severity="warning" sx={{ mt: 2 }}>
            Heads up: {v?.fidelityWarnings.length} item(s) would change if you publish now. Review them before
            publishing so you don't overwrite live data unexpectedly.
          </Alert>
        )}
        {(noop === null || v?.error) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Import succeeded. The no-op verification could not be computed{v?.error ? `: ${v.error}` : ''}.
          </Alert>
        )}

        {renderEntityTable(applied, true)}
        {renderExclusions(applied)}

        {noop === false && v && v.fidelityWarnings.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>Would change on publish</Typography>
            {v.fidelityWarnings.slice(0, 25).map((w, i) => (
              <Typography key={i} sx={{ fontSize: 12, color: colors.textSecondary, mt: 0.5 }}>
                <Chip label={w.status} size="small" sx={{ mr: 1, height: 18, fontSize: 10 }} />
                <strong>{w.category}</strong> / {w.code}
              </Typography>
            ))}
          </Box>
        )}
      </>
    );
  };

  // ── Footer actions per step ──────────────────────────────────────────────
  const footer = () => {
    if (step === 'pick') {
      return (
        <>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!selected || previewMutation.isPending}
            startIcon={previewMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => selected && previewMutation.mutate(selected.tenant_code)}
            sx={{ textTransform: 'none', bgcolor: colors.brand, color: '#1a1a1a', '&:hover': { bgcolor: colors.brandHover } }}
          >
            Preview import
          </Button>
        </>
      );
    }
    if (step === 'preview') {
      return (
        <>
          <Button onClick={() => setStep('pick')} sx={{ textTransform: 'none' }}>Back</Button>
          <WriteButton
            variant="contained"
            // Apply routes to the SELECTED env (onboarding write); its fence is
            // the gate, so the env-level read-only belt must not disable this.
            // Prod is allowed too: the server exempts /apply from the blanket
            // read-only fence and defers to requirePerTenantEditable, which
            // permits onboarding a platform-only tenant into config on ANY env
            // (see server/src/index.ts) — so the button must not blanket-block prod.
            ignoreEnvReadOnly
            disabled={applyMutation.isPending}
            startIcon={applyMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => selected && applyMutation.mutate(selected.tenant_code)}
            sx={{ textTransform: 'none', bgcolor: colors.brand, color: '#1a1a1a', '&:hover': { bgcolor: colors.brandHover } }}
          >
            Sync now
          </WriteButton>
        </>
      );
    }
    // result
    return (
      <>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Done</Button>
        <Button
          variant="contained"
          onClick={() => applied && onImported(applied.tenantCode)}
          sx={{ textTransform: 'none', bgcolor: colors.brand, color: '#1a1a1a', '&:hover': { bgcolor: colors.brandHover } }}
        >
          Open tenant
        </Button>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogContent sx={{ p: 4 }}>
        {step === 'pick' && renderPick()}
        {step === 'preview' && renderPreview()}
        {step === 'result' && renderResult()}
      </DialogContent>
      <DialogActions sx={{ px: 4, py: 2, borderTop: `1px solid ${colors.borderLight}` }}>{footer()}</DialogActions>
    </Dialog>
  );
};

export default SyncFromPlatformModal;
